import { Controller, Path } from 'react-hook-form';
import { Box, TextField } from '@mui/material';
import { parse, isValid, startOfDay, differenceInYears } from 'date-fns';
import z from 'zod';
import { ResponsiveFormRow } from '@ldt/features/orders';
import { FormSectionCard } from '@ldt/features/orders/components/form_section_card';
import { FormDatePicker } from 'src/components/date_picker';
import { SectionHeader } from 'src/components/section_header';
import { SelectList } from 'src/components/select_list';
import {
  ETHNICITY_OPTIONS,
  FormFieldProps,
} from 'src/distribution_platforms/features/order_form';
import { Ethnicity } from 'src/types';

const ETHNICITY_VALUES = Object.values(Ethnicity) as [string, ...string[]];

export const PATIENT_MIN_AGE = 18;
export const PATIENT_MAX_AGE = 100;

const patientAgeRangeMessage = `Acceptable age range for patient is from ${PATIENT_MIN_AGE} to ${PATIENT_MAX_AGE}`;

export const PatientInformationFormSchema = z.object({
  firstName: z.string().trim().min(1, 'First Name is required'),
  lastName: z.string().trim().min(1, 'Last Name is required'),
  dateOfBirth: z
    .string()
    .min(1, 'Date of Birth is required')
    .refine(
      (val) => {
        if (!val) return false;
        try {
          const date = parse(val, 'yyyy-MM-dd', new Date());
          if (!isValid(date)) return false;

          const today = startOfDay(new Date());
          const birthDate = startOfDay(date);
          const age = differenceInYears(today, birthDate);

          return age >= PATIENT_MIN_AGE && age <= PATIENT_MAX_AGE;
        } catch {
          return false;
        }
      },
      {
        message: patientAgeRangeMessage,
      },
    ),
  ethnicity: z.enum(ETHNICITY_VALUES).optional().or(z.literal('')),
});

type PatientInformationFormData = z.infer<typeof PatientInformationFormSchema>;

export const PatientInformationFormSection = <
  T extends PatientInformationFormData,
>({
  control,
  errors,
}: FormFieldProps<T>) => (
  <FormSectionCard
    header={<SectionHeader title='Patient Information' />}
    sx={{ borderBottom: 'none' }}
  >
    <Box
      data-testid='patient-information-form-section'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <ResponsiveFormRow>
        <Box
          sx={{
            flex: 1,
          }}
        >
          <Controller
            name={'firstName' as Path<T>}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                required
                fullWidth
                label='First Name'
                data-testid='patient-information-first-name'
                error={!!errors.firstName}
                helperText={errors.firstName?.message?.toString() ?? ' '}
              />
            )}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
          }}
        >
          <Controller
            name={'lastName' as Path<T>}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                required
                fullWidth
                label='Last Name'
                data-testid='patient-information-last-name'
                error={!!errors.lastName}
                helperText={errors.lastName?.message?.toString() ?? ' '}
              />
            )}
          />
        </Box>
      </ResponsiveFormRow>
      <ResponsiveFormRow>
        <Box
          data-testid='patient-information-date-of-birth'
          sx={{
            flex: 1,
          }}
        >
          <Controller
            name={'dateOfBirth' as Path<T>}
            control={control}
            render={({ field }) => (
              <FormDatePicker
                label='Date of Birth'
                maxDate={new Date()}
                value={field.value}
                required
                fullWidth
                error={!!errors.dateOfBirth}
                helperText={errors.dateOfBirth?.message?.toString() ?? ' '}
                onChange={field.onChange}
                onBlur={field.onBlur}
              />
            )}
          />
        </Box>
        <Box
          data-testid='patient-information-ethnicity'
          sx={{
            flex: 1,
          }}
        >
          <Controller
            name={'ethnicity' as Path<T>}
            control={control}
            render={({ field }) => (
              <SelectList
                {...field}
                label='Ethnicity'
                labelId='ethnicity-label'
                options={ETHNICITY_OPTIONS}
                validationText={errors.ethnicity?.message?.toString() ?? ''}
              />
            )}
          />
        </Box>
      </ResponsiveFormRow>
    </Box>
  </FormSectionCard>
);
